import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import RestoPayment from './RestoPayment';
import { resolvePaymentDestination } from './paymentGatewayRouting';

const API_URL = (process.env.REACT_APP_API_URL || 'https://api.willovate.in').replace(/\/$/, '');

const shellStyle = {
  minHeight: '100vh',
  display: 'grid',
  placeItems: 'center',
  padding: 24,
  background: 'linear-gradient(135deg, #071329 0%, #102a56 100%)',
  fontFamily: 'Inter, system-ui, sans-serif',
};

const cardStyle = {
  width: 'min(92vw, 420px)',
  padding: 28,
  borderRadius: 18,
  color: '#dbeafe',
  textAlign: 'center',
  background: 'rgba(7, 19, 41, 0.9)',
  border: '1px solid rgba(147, 197, 253, 0.2)',
  boxShadow: '0 24px 70px rgba(0, 0, 0, 0.35)',
};

export default function PaymentGatewayRouter() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [provider, setProvider] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  const loadProvider = useCallback(async (signal) => {
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/payment/config`, {
        headers: { Accept: 'application/json' },
        signal,
      });
      const body = await response.json().catch(() => null);
      const activeProvider = body?.data?.activeProvider;
      if (!response.ok || body?.success !== true || !['PhonePe', 'Razorpay'].includes(activeProvider)) {
        throw new Error(body?.message || 'Payment gateway configuration is unavailable.');
      }
      setProvider(activeProvider);
    } catch (reason) {
      if (reason?.name !== 'AbortError') {
        setError(reason instanceof Error ? reason.message : 'Could not load the payment gateway.');
      }
    }
  }, []);

  useEffect(() => {
    // A pre-initiated PhonePe URL is already bound to a PhonePe transaction and
    // must finish there even if Admin switches the global provider mid-payment.
    if (searchParams.has('phonePeUrl') || searchParams.has('merchantTransactionId')) {
      setProvider('PhonePe');
      return undefined;
    }

    const controller = new AbortController();
    void loadProvider(controller.signal);
    return () => controller.abort();
  }, [attempt, loadProvider, searchParams]);

  if (provider === 'PhonePe') return <RestoPayment />;
  if (provider === 'Razorpay') {
    return <Navigate to={resolvePaymentDestination(provider, location.search)} replace />;
  }

  return (
    <main style={shellStyle}>
      <section style={cardStyle} aria-live="polite">
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#67e8f9' }}>
          Willovate Secure Payment
        </div>
        {error ? (
          <>
            <h1 style={{ margin: '18px 0 8px', color: '#fff', fontSize: 22 }}>Checkout is temporarily unavailable</h1>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: 13, lineHeight: 1.7 }}>{error}</p>
            <button
              type="button"
              onClick={() => { setProvider(null); setAttempt((value) => value + 1); }}
              style={{ marginTop: 20, border: 0, borderRadius: 10, padding: '11px 18px', background: '#2563eb', color: '#fff', fontWeight: 750, cursor: 'pointer' }}
            >
              Try again
            </button>
          </>
        ) : (
          <>
            <div style={{ width: 34, height: 34, margin: '22px auto 14px', borderRadius: '50%', border: '3px solid rgba(103,232,249,.2)', borderTopColor: '#67e8f9' }} />
            <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>Preparing your selected payment gateway…</p>
          </>
        )}
      </section>
    </main>
  );
}
